import React, { ReactElement, useContext, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import DateAndTimeBlock from './DateAndTimeBlock'
import RatingsAndPriceBlock from './RatingsAndPriceBlock'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EntryField } from './Daily'
import { Entry } from '../Entry/Entry.d'
import { AppContext } from '../contexts/AppContext'
import { isUsingField } from '../contexts/app/auth'
import { authenticationFailed } from '../site/Login'
import { EntryContext } from '../contexts/EntryContext'
import { VoiceTranscript } from './VoiceRecord'
import { FormUpdated, SetValue, SimpleWriteProps } from './Write.d'
import { faQuoteLeft, faListAlt, faMapMarkerAlt, faExclamationTriangle, faMicrophone,
    faCircleStop, faCloudArrowDown, faUpload, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { entryStatus } from '../Entry/EntryStatusIcons'
library.add(faQuoteLeft, faListAlt, faMapMarkerAlt, faExclamationTriangle, faMicrophone,
    faCircleStop, faCloudArrowDown, faUpload, faArrowLeft, faArrowRight)

const FormFields = (props: { entry: Entry, setValue: SetValue, formUpdated: FormUpdated }): ReactElement => {
    const { appState } = useContext(AppContext)
    const { state } = useContext(EntryContext)
    const { isLoading, isAuthenticated } = useAuth0()
    const [previewingServer, setPreviewingServer] = useState(false)

    const serverEntry = props.entry.saveStatus === 'conflicted' ? state.entries?.[props.entry.id] : undefined
    if (!serverEntry && previewingServer) setPreviewingServer(false)
    const displayEntry = previewingServer && serverEntry ? serverEntry : props.entry
    const disabled = props.entry.status === 'deleted' || !!serverEntry && previewingServer

    const loginIssue = authenticationFailed(isLoading, isAuthenticated, appState)
    const fieldsetClass = loginIssue
        ? 'form-warning'
        : appState.offlineMode
            ? 'form-offline'
            : `entry-${entryStatus(props.entry, state) || 'fresh'}`
    return <fieldset className={ fieldsetClass } disabled={ disabled }>

        {/* Title, location */}
        { isUsingField('title', appState) && <TitleField entry={ displayEntry } setValue={ props.setValue } /> }
        { isUsingField('loc', appState) && <LocationField entry={ displayEntry } setValue={ props.setValue } />}

        {/* Date(s), type, start time, end time */}
        <DateAndTimeBlock entry={ displayEntry } setValue={ props.setValue } formUpdated={ props.formUpdated } />

        {/* Ratings, price */}
        <RatingsAndPriceBlock entry={ displayEntry } setValue={ props.setValue } />

        { isUsingField('voice', appState) && <VoiceTranscript
            entry={ displayEntry }
            setValue={ props.setValue }
            formUpdated={ props.formUpdated } />}

        {/* Summary, entry */}
        { isUsingField('summary', appState) && <SummaryField entry={ displayEntry } setValue={ props.setValue } /> }

        <ModificationConflictMessage
            entry={ props.entry }
            previewingServer={ previewingServer }
            setPreviewingServer={ setPreviewingServer } />
        <EntryField entry={ displayEntry } setValue={ props.setValue } />
    </fieldset>
}

const TitleField = (props: SimpleWriteProps) =>
    <div className="entry-write-descriptor">
        <input
            id="title"
            value={ props.entry.title || '' }
            placeholder="e.g. My first entry"
            onChange={ e => props.setValue('title', e.target.value) } />
        <label htmlFor="title"><FontAwesomeIcon icon="quote-left" /></label>
    </div>

const LocationField = (props: SimpleWriteProps) =>
    <div className="entry-write-descriptor">
        <input
            id="loc"
            value={ props.entry.loc || '' }
            placeholder="e.g. London; Gare de Lyon, Paris"
            onChange={ e => props.setValue('loc', e.target.value) } />
        <label htmlFor="loc"><FontAwesomeIcon icon="map-marker-alt" /></label>
    </div>

const SummaryField = (props: SimpleWriteProps) =>
    <div className="entry-write-descriptor">
        <textarea
            role="textbox"
            id="summary"
            className="summary"
            value={ props.entry.summary || '' }
            placeholder={ 'e.g.\n- 10:00 Meeting.\n- 18:00 Family call.' }
            onChange={ e => props.setValue('summary', e.target.value) } />
        <label htmlFor="summary"><FontAwesomeIcon icon="list-alt" /></label>
    </div>

type ConflictProps = {
    entry: Entry
    previewingServer: boolean
    setPreviewingServer: (previewing: boolean) => void
}

const ModificationConflictMessage = (props: ConflictProps): ReactElement | null => {
    const { dispatch } = useContext(EntryContext)
    if (props.entry.saveStatus === 'conflicted') {
        const overwrite = () => dispatch({ type: 'ENTRY_CONFLICT_OVERWRITE', id: props.entry.id })
        const discard = () => dispatch({ type: 'ENTRY_CONFLICT_DISCARD', id: props.entry.id })
        const togglePreview = () => props.setPreviewingServer(!props.previewingServer)
        return <div role="alert" id="modification-conflict-warning" className="tip error">
            <FontAwesomeIcon icon="exclamation-triangle" />
            Warning: this entry may have been modified on another device.<br />
            You can overwrite those changes with the ones you have here,
            or discard your local modifications.
            <div className="conflict-actions">
                <div className="clickable" onClick={ overwrite }><FontAwesomeIcon icon="upload" /> Overwrite</div>
                <div className="clickable" onClick={ togglePreview }>
                    { props.previewingServer
                        ? <>Showing conflict <FontAwesomeIcon icon="arrow-right" /></>
                        : <><FontAwesomeIcon icon="arrow-left" /> Showing yours</> }
                </div>
                <div className="clickable" onClick={ discard }><FontAwesomeIcon icon="cloud-arrow-down" /> Discard changes</div>
            </div>
        </div>
    }
    return null
}

export default FormFields
